import {jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs} from "react/jsx-runtime";import {lazy, memo, Suspense} from 'react'
import 'react-day-picker/dist/style.css'
import {Route, Routes} from 'react-router'
import useServiceWorkerUpdater from '../../hooks/useServiceWorkerUpdater'
import {LoaderSize} from '../../types/constEnums'
import {cn} from '../../ui/cn'
import {CREATE_ACCOUNT_SLUG, SIGNIN_SLUG} from '../../utils/constants'
import ErrorBoundary from '../ErrorBoundary'
import Banner from '../GlobalBanner'
import {useIsAuthenticated} from '../IsAuthenticatedProvider'
import LoadingComponent from '../LoadingComponent/LoadingComponent'
import PrivateRoutes from '../PrivateRoutes'
import Snackbar from '../Snackbar'

const AnalyticsPage = lazy(() => import(/* webpackChunkName: 'AnalyticsPage' */ '../AnalyticsPage'))
const AuthenticationPage = lazy(
  () => import(/* webpackChunkName: 'AuthenticationPage' */ '../AuthenticationPage')
)
const DemoMeeting = lazy(() => import(/* webpackChunkName: 'DemoMeeting' */ '../DemoMeeting'))
const TeamHealthDemoPage = lazy(
  () => import(/* webpackChunkName: 'TeamHealthDemoPage' */ '../TeamHealthDemo/TeamHealthDemoPage')
)
const DemoSummary = lazy(() => import(/* webpackChunkName: 'DemoSummary' */ '../DemoSummary'))
const AuthProvider = lazy(() => import(/* webpackChunkName: 'AuthProvider' */ '../AuthProvider'))
const SAMLRedirect = lazy(() => import(/* webpackChunkName: 'SAMLRedirect' */ '../SAMLRedirect'))
const SetNewPassword = lazy(
  () => import(/* webpackChunkName: 'SetNewPassword' */ '../ResetPasswordPage/SetNewPassword')
)
const VerifyEmail = lazy(() => import(/* webpackChunkName: 'VerifyEmail' */ '../VerifyEmail'))
const TeamInvitation = lazy(
  () => import(/* webpackChunkName: 'TeamInvitationRoot' */ '../TeamInvitationRoot')
)
const InvitationLink = lazy(
  () => import(/* webpackChunkName: 'InvitationLinkRoot' */ '../InvitationLinkRoot')
)
const PageRoot = lazy(
  () => import(/* webpackChunkName: 'PageRoot' */ '../../modules/pages/PageRoot')
)
const OAuthAuthorizePage = lazy(
  () => import(/* webpackChunkName: 'OAuthAuthorizePage' */ '../OAuthAuthorizePage')
)

const Action = memo(() => {
  useServiceWorkerUpdater()
  const isInternalAuthEnabled = window.__ACTION__.AUTH_INTERNAL_ENABLED
  // Global Banner
  const isGlobalBannerEnabled = window.__ACTION__.GLOBAL_BANNER_ENABLED
  const bannerText = window.__ACTION__.GLOBAL_BANNER_TEXT
  const bannerBgColor = window.__ACTION__.GLOBAL_BANNER_BG_COLOR
  const bannerColor = window.__ACTION__.GLOBAL_BANNER_COLOR
  // /pages must have the same stable path as the other dashboard routes
  // to preserve the internal state of the left nav (i.e. which team & pages are expanded)
  const isAuthenticated = useIsAuthenticated()

  return (
    _jsx(_Fragment, { children: 
      _jsxs(ErrorBoundary, { children: [
        _jsx(Snackbar, {} )
        , _jsxs(Suspense, { fallback: _jsx(LoadingComponent, { spinnerSize: LoaderSize.WHOLE_PAGE,} ), children: [
          _jsx(AnalyticsPage, {} )
          , isGlobalBannerEnabled && (
            _jsx(Banner, { bgColor: bannerBgColor, color: bannerColor, text: bannerText,} )
          )
          , _jsx('div', {
            className: cn(
              'flex h-screen w-full flex-col print:h-auto',
              isGlobalBannerEnabled && `h-[calc(100vh-var(--global-banner-height))]`
            ),
 children: 
            _jsxs(Routes, { children: [
              _jsx(Route, { path: "/", element: _jsx(AuthenticationPage, { page: 'signin',} ),} )
              , _jsx(Route, { path: `/${SIGNIN_SLUG}`, element: _jsx(AuthenticationPage, { page: 'signin',} ),} )
              , _jsx(Route, {
                path: `/${CREATE_ACCOUNT_SLUG}`,
                element: _jsx(AuthenticationPage, { page: 'create-account',} ),}
              )
              , _jsx(Route, { path: "/auth/:provider", element: _jsx(AuthProvider, {} ),} )
              , _jsx(Route, { path: "/saml-redirect", element: _jsx(SAMLRedirect, {} ),} )
              , _jsx(Route, { path: "/retrospective-demo/*", element: _jsx(DemoMeeting, {} ),} )
              , _jsx(Route, { path: "/team-health-demo/*", element: _jsx(TeamHealthDemoPage, {} ),} )
              , _jsx(Route, { path: "/retrospective-demo-summary/:urlAction", element: _jsx(DemoSummary, {} ),} )
              , _jsx(Route, { path: "/retrospective-demo-summary", element: _jsx(DemoSummary, {} ),} )
              , isInternalAuthEnabled && (
                _jsx(Route, {
                  path: "/forgot-password",
                  element: _jsx(AuthenticationPage, { page: 'forgot-password',} ),}
                )
              )
              , isInternalAuthEnabled && (
                _jsx(Route, {
                  path: "/forgot-password/submitted",
                  element: _jsx(AuthenticationPage, { page: 'forgot-password/submitted',} ),}
                )
              )
              , _jsx(Route, {
                path: "/verify-email/:verificationToken/:invitationToken",
                element: _jsx(VerifyEmail, {} ),}
              )
              , _jsx(Route, { path: "/verify-email/:verificationToken", element: _jsx(VerifyEmail, {} ),} )
              , _jsx(Route, { path: "/reset-password/:token", element: _jsx(SetNewPassword, {} ),} )
              , _jsx(Route, { path: "/oauth/authorize", element: _jsx(OAuthAuthorizePage, {} ),} )
              , _jsx(Route, { path: "/team-invitation/:token", element: _jsx(TeamInvitation, {} ),} )
              , _jsx(Route, { path: "/invitation-link/:token", element: _jsx(InvitationLink, {} ),} )
              , !isAuthenticated && (
                _jsx(Route, { path: "/pages/:pageSlug", element: _jsx(PageRoot, { viewerRef: null, isPublic: true,} ),} )
              )
              , _jsx(Route, { path: "/*", element: _jsx(PrivateRoutes, {} ),} )
            ]})
          })
        ]})
      ]})
    })
  )
})

Action.displayName = 'Action'

export default Action
